// Companion/chatBlockScopeActions.ts
//
// What changes a QBank block's chat scope (ORA-1285): the composer pill's menu,
// the continue offer, and the companion's `keep_chat_for_block` action. Each one
// tells the server first, then switches the block's scope and files the
// conversation on screen under the thread key it now belongs to, so the
// drawer's resolver finds its target reached and does not re-read it.

import type { Dispatch } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';
import {
  addChatBlockScopeNotice,
  getChatBlockThreadState,
  markChatBlockOfferShown,
  rememberQuestionScopeChoice,
  setChatBlockOffer,
  setChatBlockScopeEntry,
  type ChatBlockOffer,
  type ChatBlockScope,
} from '@/chatContext/chatBlockThread';
import { getChatAppContextInputs } from '@/chatContext/chatAppContextStore';
import {
  conversationAnchorKey,
  toConversationAnchor,
} from '@/chatContext/chatThreadKey';
import {
  releaseChatBlockThread,
  setChatBlockThread,
} from '@/services/chatBlockThreads';
import type { ChatConversationDetail } from '@/services/chatConversations';
import {
  conversationBlockThreadSet,
  conversationHydrated,
  isDraftConversationId,
  threadHistorySelectionStarted,
  threadResolutionFinished,
} from '@/slices/chat/chatSlice';
import {
  selectActiveTurn,
  selectConversationMessageIds,
  type ChatRootState,
} from '@/slices/chat/chatSelectors';
import { capture } from '@/utils/analytics';
import { toHydratedConversation } from './chatConversationHydration';

export interface ChatBlockScopeStore {
  dispatch: Dispatch;
  getState: () => ChatRootState;
}

/** Where a scope change came from, for `chat_block_scope_changed`. */
export type ChatBlockScopeVia =
  'pill' | 'offer' | 'action' | 'settings' | 'always';

const CHANGE_FAILED = 'That could not be changed. Please try again.';
const BLOCK_REFUSED = 'This block cannot keep one chat.';

const CHAT_NOT_KEPT =
  'Your current chat could not be kept, so a new block chat will start with your next message.';

export const captureBlockScopeChange = (
  from: ChatBlockScope,
  to: ChatBlockScope,
  via: ChatBlockScopeVia
): void => {
  capture('chat_block_scope_changed', { from, to, via });
};

/** The thread key of a block's one chat. */
export const blockThreadKey = (blockId: string): string =>
  conversationAnchorKey({ type: 'qbank_block', id: blockId });

/** The thread key of the question on screen. */
const surfaceQuestionKey = (): string =>
  conversationAnchorKey(
    toConversationAnchor(getChatAppContextInputs().surfaceAnchor)
  );

let filedCount = 0;

/**
 * Make a loaded conversation the thread for `key` without a re-read: the
 * resolver sees its target reached and leaves it alone (the document viewer
 * files a conversation the same way).
 */
const fileConversation = (
  store: ChatBlockScopeStore,
  key: string,
  conversationId: string
): void => {
  const resolutionId = `block-thread-${++filedCount}`;

  store.dispatch(
    threadHistorySelectionStarted({ key, resolutionId, conversationId })
  );

  store.dispatch(threadResolutionFinished({ key, resolutionId }));
};

const lastMessageId = (
  state: ChatRootState,
  conversationId: string
): string | null => {
  const messageIds = selectConversationMessageIds(state, conversationId);
  return messageIds[messageIds.length - 1] ?? null;
};

const leaveNotice = (
  store: ChatBlockScopeStore,
  conversationId: string,
  scope: ChatBlockScope
): void => {
  const afterMessageId = lastMessageId(store.getState(), conversationId);
  if (afterMessageId) {
    addChatBlockScopeNotice(conversationId, { afterMessageId, scope });
  }
};

/** Hydrate what the server returned and file it under the block's key. */
const adoptBlockThread = (
  store: ChatBlockScopeStore,
  blockId: string,
  detail: ChatConversationDetail
): void => {
  store.dispatch(conversationHydrated(toHydratedConversation(detail)));
  fileConversation(store, blockThreadKey(blockId), detail.id);
};

/** The conversation on screen, when it has a server id and messages. */
const startedConversationId = (state: ChatRootState): string | null => {
  const id = state.chat.activeConversationId;
  if (!id || isDraftConversationId(id)) return null;

  return selectConversationMessageIds(state, id).length > 0 ? id : null;
};

/**
 * A turn running in the conversation on screen. A scope change waits for it:
 * moving the thread mid-answer would strand the answer in the old one.
 */
export const isScopeChangeBlocked = (state: ChatRootState): boolean => {
  const turn = selectActiveTurn(state, state.chat.activeConversationId);
  return turn?.status === 'requested' || turn?.status === 'streaming';
};

/** One change at a time: a second tap while the first is in flight is ignored. */
let isChanging = false;

const runExclusive = async (change: () => Promise<boolean>) => {
  if (isChanging) return false;
  isChanging = true;
  try {
    return await change();
  } catch (error) {
    enqueueSnackbar(CHANGE_FAILED, { variant: 'error' });
    console.error('Unable to change the block chat', error);
    return false;
  } finally {
    isChanging = false;
  }
};

/**
 * The server would not take this conversation for the block. A refused block
 * gets no block chat on this page; a refused conversation only means the block
 * chat starts fresh, with the student's next message (`threadScope`). Resolves
 * true when the block is now `block`.
 */
const handleRefusal = (
  blockId: string,
  reason: 'block_ineligible' | 'conversation_not_found'
): boolean => {
  if (reason === 'block_ineligible') {
    setChatBlockScopeEntry(blockId, 'ineligible');
    enqueueSnackbar(BLOCK_REFUSED, { variant: 'info' });
    return false;
  }

  rememberQuestionScopeChoice(blockId, false);
  setChatBlockScopeEntry(blockId, 'block');
  enqueueSnackbar(CHAT_NOT_KEPT, { variant: 'info' });
  return true;
};

/**
 * "The whole block": the chat on screen becomes the block's thread. A chat
 * with no messages yet just switches the key; the block's first turn creates
 * the thread (`threadScope`). Resolves true when the block is now `block`.
 */
export const switchToBlockScope = (
  store: ChatBlockScopeStore,
  via: ChatBlockScopeVia
): Promise<boolean> =>
  runExclusive(async () => {
    const context = getChatBlockThreadState().current;
    if (!context || isScopeChangeBlocked(store.getState())) return false;

    const { blockId } = context;
    const conversationId = startedConversationId(store.getState());

    const result = conversationId
      ? await setChatBlockThread(conversationId, blockId)
      : null;

    if (result && !result.ok) {
      const switched = handleRefusal(blockId, result.reason);
      if (switched && via === 'pill') {
        captureBlockScopeChange('question', 'block', via);
      }
      return switched;
    }

    // The scope first, so the drawer's key is the block's when the
    // conversation is filed under it.
    rememberQuestionScopeChoice(blockId, false);
    setChatBlockScopeEntry(blockId, 'block');

    if (result) {
      adoptBlockThread(store, blockId, result.conversation);
      leaveNotice(store, result.conversation.id, 'block');
    }

    if (via === 'pill') captureBlockScopeChange('question', 'block', via);
    return true;
  });

/**
 * "This question": the block's thread goes back to being the chat of the
 * question it started on, and stays on screen until the student moves on.
 * From the next question each one has its own chat again.
 */
export const switchToQuestionScope = (
  store: ChatBlockScopeStore
): Promise<boolean> =>
  runExclusive(async () => {
    const context = getChatBlockThreadState().current;
    if (!context || isScopeChangeBlocked(store.getState())) return false;

    const { blockId } = context;
    const conversationId = startedConversationId(store.getState());
    if (conversationId) await releaseChatBlockThread(conversationId);

    leaveBlockScope(blockId);

    if (conversationId) {
      store.dispatch(
        conversationBlockThreadSet({
          id: conversationId,
          questionBlockId: null,
        })
      );

      fileConversation(store, surfaceQuestionKey(), conversationId);
      leaveNotice(store, conversationId, 'question');
    }

    captureBlockScopeChange('block', 'question', 'pill');
    return true;
  });

/** As if the student had picked "This question" for the block. */
const leaveBlockScope = (blockId: string): void => {
  rememberQuestionScopeChoice(blockId, true);
  // They chose a chat per question; offering to merge them again would argue
  // with that.
  markChatBlockOfferShown(blockId);
  setChatBlockScopeEntry(blockId, 'question');
};

/**
 * A conversation picked from history while the block keeps one chat. The
 * picked chat is not the block's, so carrying it from question to question
 * would make it one; the block goes back to a chat per question instead (no
 * server call: the block's thread stays the block's). Returns the thread key
 * to open the pick under, or null when the block scope is not involved.
 */
export const leaveBlockScopeForHistoryPick = (
  store: ChatBlockScopeStore,
  conversationId: string
): string | null => {
  const { current, scopes } = getChatBlockThreadState();
  if (!current || scopes[current.blockId] !== 'block') return null;
  if (conversationId === store.getState().chat.activeConversationId)
    return null;

  const picked = store.getState().chat.conversations[conversationId];
  if (picked?.questionBlockId === current.blockId) return null;

  leaveBlockScope(current.blockId);
  captureBlockScopeChange('block', 'question', 'pill');
  return surfaceQuestionKey();
};

/** "Continue": the previous question's chat becomes the block's thread. */
export const continueFromOffer = (
  store: ChatBlockScopeStore,
  offer: ChatBlockOffer
): Promise<boolean> =>
  runExclusive(async () => {
    // The same guards as the pill: no move mid-answer, and only in the block
    // the offer was made in.
    if (isScopeChangeBlocked(store.getState())) return false;
    if (getChatBlockThreadState().current?.blockId !== offer.blockId) {
      setChatBlockOffer(null);
      return false;
    }

    const result = await setChatBlockThread(
      offer.fromConversationId,
      offer.blockId
    );

    setChatBlockOffer(null);

    if (!result.ok) {
      const switched = handleRefusal(offer.blockId, result.reason);
      if (switched) captureBlockScopeChange('question', 'block', 'offer');
      return switched;
    }

    rememberQuestionScopeChoice(offer.blockId, false);
    setChatBlockScopeEntry(offer.blockId, 'block');
    adoptBlockThread(store, offer.blockId, result.conversation);
    leaveNotice(store, result.conversation.id, 'block');
    captureBlockScopeChange('question', 'block', 'offer');
    return true;
  });

/**
 * The companion's `keep_chat_for_block` ran: the server has already made the
 * conversation the block's thread, and its card shows the outcome, so this
 * only follows it.
 */
export const followBlockThreadAction = (
  store: ChatBlockScopeStore,
  blockId: string,
  conversationId: string
): void => {
  rememberQuestionScopeChoice(blockId, false);
  setChatBlockScopeEntry(blockId, 'block');

  store.dispatch(
    conversationBlockThreadSet({ id: conversationId, questionBlockId: blockId })
  );

  // Filed only while the student is still in that block; elsewhere the scope
  // alone is enough for when they come back to it.
  if (getChatBlockThreadState().current?.blockId === blockId) {
    fileConversation(store, blockThreadKey(blockId), conversationId);
  }

  captureBlockScopeChange('question', 'block', 'action');
};
